/**
 * Pipeline drag-and-drop mutations + required-checklist gate.
 *
 * The Phase 2 KanbanBoard uses this to:
 *   1. Move a deal between pipelines (stages).
 *   2. Block "advance to later stage" moves when the source stage has
 *      `required` default_checklists items that haven't been completed for
 *      the deal. Cross-stage moves to EARLIER or laterally are still allowed
 *      (so users can recover from mis-clicks without unwinding their work).
 *
 * Pipelines are ordered by their `order` column; "advance" = moving to a
 * pipeline with a strictly larger order than the source.
 *
 * The gate uses `default_checklists` + `pipeline_property_checklists` joined
 * at read time — no extra round trips at drop time; we just consult the data
 * already fetched by `usePipelinePropertyChecklist` for the deal.
 */
import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import type { LoanType } from "@/hooks/use-loans";
import type { SavedProperty } from "@/hooks/use-saved-properties";
import { useWorkspace } from "@/hooks/use-workspace";
import {
  LISTING_STATUS_LABELS,
  blocksAcquisition,
  resolveListingStatus,
  type ListingStatus,
  type ListingStatusSource,
} from "@/lib/listing-status";
import { splitStrategySettings, type StrategyPatch } from "@/lib/property-setting";
import { STAGE_HISTORY_QUERY_PREFIX } from "@/lib/query-keys";
import { toUserMessage } from "@/lib/supabase-errors";
import type {
  DefaultChecklistRow,
  PipelinePropertyChecklistRow,
  PipelineRow,
} from "@/types/pipeline";

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;

interface MoveDealArgs {
  pipelinePropertyId: string;
  /** Source pipeline (current). */
  fromPipeline: PipelineRow;
  /** Target pipeline (new). */
  toPipeline: PipelineRow;
}

/** `property_setting` patch carried by the Closed-Won acquisition modal. */
type ClosedWonSettingsPatch = { purchase_price?: number | null } & StrategyPatch;

interface MoveToClosedWonArgs {
  pipelinePropertyId: string;
  /** Source pipeline (current). */
  fromPipeline: PipelineRow;
  /** Target pipeline — the Closed-Won stage. */
  toPipeline: PipelineRow;
  /** Resolved acquisition date (YYYY-MM-DD). */
  ownedDate: string;
  /** Confirmed purchase price (written to pipeline_properties + property_setting). */
  purchasePrice: number | null;
  /** property_setting patch (purchase_price + acquisition loan blob), or null. */
  dealSettingsPatch: ClosedWonSettingsPatch | null;
  /**
   * Financing to materialise in the `loans` table now that the deal is owned —
   * `loan_type` by strategy, `is_enabled` reflecting the user's choice. null
   * when the deal has no strategy. The loans table is the calculation source of
   * truth; it's written here (at close), never before.
   */
  financing: { loan_type: LoanType; enabled: boolean } | null;
}

export interface MoveDealResult {
  /**
   * "blocked" = unmet required checklist items. "sold" = the feed says another
   * buyer already closed on it, so it can never be marked owned.
   */
  status: "ok" | "blocked" | "sold";
  blockers?: string[];
  /** The listing status that refused the move (only set when status === "sold"). */
  listingStatus?: ListingStatus;
  /** The freshly updated row from the DB (only set when status === "ok"). */
  row?: SavedProperty;
}

/**
 * Re-read the deal's market status straight from the DB.
 *
 * The board already checks its cached copy before opening the acquisition modal;
 * this is the authoritative pass. It exists because the cache can be minutes old
 * — the refresh job or another tab may have marked the listing sold in between —
 * and stamping `is_owned` on a property somebody else bought is not recoverable
 * by a retry.
 */
async function fetchListingStatus(
  pipelinePropertyId: string,
): Promise<ListingStatus> {
  const client = supabase as unknown as {
    from: (t: string) => {
      select: (s: string) => {
        eq: (
          c: string,
          v: string,
        ) => { maybeSingle: () => DbResp<ListingStatusSource | null> };
      };
    };
  };
  const { data, error } = await client
    .from("pipeline_properties")
    .select("listing_status, property_data")
    .eq("id", pipelinePropertyId)
    .maybeSingle();
  if (error) throw error;
  return resolveListingStatus(data ?? {});
}

/**
 * Returns the labels of required checklist items on the source pipeline that
 * the deal hasn't completed. Empty array = move is allowed.
 *
 * Caller must pre-fetch:
 *   - `defaults` — `default_checklists` rows for `fromPipeline.id`
 *   - `state` — `pipeline_property_checklists` rows for the deal scoped to
 *               `fromPipeline.id`
 */
export function findUnmetRequired(
  defaults: DefaultChecklistRow[],
  state: PipelinePropertyChecklistRow[],
): string[] {
  // Latest state row per default_checklist_id (no UNIQUE constraint).
  const byDefault = new Map<string, PipelinePropertyChecklistRow>();
  for (const s of state) {
    if (!s.default_checklist_id) continue;
    const prev = byDefault.get(s.default_checklist_id);
    if (!prev) {
      byDefault.set(s.default_checklist_id, s);
      continue;
    }
    const aT = prev.updated_at ?? prev.created_at;
    const bT = s.updated_at ?? s.created_at;
    if (bT > aT) byDefault.set(s.default_checklist_id, s);
  }

  const unmet: string[] = [];
  for (const d of defaults) {
    if (!d.required) continue;
    const s = byDefault.get(d.id);
    const isInput = d.item_type === "input";
    const ok = isInput
      ? Boolean(s?.checklist_text && s.checklist_text.trim())
      : s?.checked === true;
    if (!ok) unmet.push(d.checklist_text);
  }
  return unmet;
}

/** Whether `toPipeline` is strictly later in the kanban than `fromPipeline`. */
function isAdvance(from: PipelineRow, to: PipelineRow): boolean {
  return to.order > from.order;
}

/**
 * Fetch the source stage's required checklist items + this deal's state and
 * return the labels of any unmet required items. Empty array = move allowed.
 * Shared by the plain move and the Closed-Won move so both gate identically.
 */
async function fetchAdvanceBlockers(
  fromPipelineId: string,
  pipelinePropertyId: string,
): Promise<string[]> {
  const [defaultsResp, stateResp] = await Promise.all([
    (
      supabase as unknown as {
        from: (t: string) => {
          select: (s: string) => {
            eq: (col: string, v: string) => DbResp<DefaultChecklistRow[]>;
          };
        };
      }
    )
      .from("default_checklists")
      .select("*")
      .eq("pipeline_id", fromPipelineId) as DbResp<DefaultChecklistRow[]>,

    (
      supabase as unknown as {
        from: (t: string) => {
          select: (s: string) => {
            eq: (col: string, v: string) => {
              eq: (
                col: string,
                v: string,
              ) => DbResp<PipelinePropertyChecklistRow[]>;
            };
          };
        };
      }
    )
      .from("pipeline_property_checklists")
      .select("*")
      .eq("pipeline_property_id", pipelinePropertyId)
      .eq("pipeline_id", fromPipelineId) as DbResp<
      PipelinePropertyChecklistRow[]
    >,
  ]);

  if (defaultsResp.error) throw defaultsResp.error;
  if (stateResp.error) throw stateResp.error;

  return findUnmetRequired(defaultsResp.data ?? [], stateResp.data ?? []);
}

export function usePipelineMutations() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  /**
   * Move a deal from one pipeline to another, with required-checklist gating.
   * Returns `{ status: "blocked", blockers: [...] }` when the move would
   * advance past unmet required items; the caller surfaces a toast.
   */
  const moveDeal = useCallback(
    async (args: MoveDealArgs): Promise<MoveDealResult> => {
      if (args.fromPipeline.id === args.toPipeline.id) {
        return { status: "ok" };
      }

      // Only gate on advance. Earlier/lateral moves are unconditionally allowed.
      if (isAdvance(args.fromPipeline, args.toPipeline)) {
        const blockers = await fetchAdvanceBlockers(
          args.fromPipeline.id,
          args.pipelinePropertyId,
        );
        if (blockers.length > 0) {
          return { status: "blocked", blockers };
        }
      }

      // Perform the move with RETURNING *. Pipeline name is resolved at read
      // time via JOIN to pipelines.title; we only write the FK.
      //
      // `.select("*").single()` is critical: without RETURNING, a silently
      // denied RLS write (or a non-matching id) would succeed with 0 rows
      // affected — no error thrown — and the subsequent refetch would
      // overwrite our optimistic cache with the OLD row, causing the card
      // to snap back. `.single()` errors out with PGRST116 when 0 rows
      // match, which we surface via the normal `onError` rollback path.
      const { data: updatedRow, error: updErr } = (await supabase
        .from("pipeline_properties")
        .update({
          pipeline_id: args.toPipeline.id,
        } as never)
        .eq("id", args.pipelinePropertyId)
        .select("*")
        .single()) as PostgrestSingleResponse<SavedProperty>;
      if (updErr) throw updErr;
      if (!updatedRow) {
        throw new Error(
          "Move failed: the deal was not found or you don't have permission to update it.",
        );
      }

      // Audit log (best-effort).
      if (user?.id) {
        const { error: histErr } = await supabase
          .from("pipeline_stage_history")
          .insert({
            pipeline_property_id: args.pipelinePropertyId,
            from_pipeline_id: args.fromPipeline.id,
            to_pipeline_id: args.toPipeline.id,
            changed_by: user.id,
            workspace_id: currentWorkspace?.id ?? null,
          } as never);
        if (histErr) {
          console.error("pipeline_stage_history insert:", histErr);
        }
      }

      return { status: "ok", row: updatedRow };
    },
    [user?.id, currentWorkspace?.id],
  );

  /**
   * Mutation wrapper around moveDeal. Surfaces a toast on "blocked" (required
   * items unmet) and re-throws on real errors. Callers that want to inspect
   * the blocker reason should call `moveDeal` directly.
   *
   * Optimistic + reconciled-via-RETURNING (no refetch race):
   *   - `onMutate` writes the card's new `pipeline_id` into the
   *     `["pipeline_properties", workspaceId]` cache so the kanban jumps to
   *     the target column instantly.
   *   - `mutationFn` runs the UPDATE with `RETURNING *` and returns the
   *     fresh row.
   *   - `onSuccess` overwrites the optimistic stub with the authoritative
   *     server row — no invalidate-and-refetch needed, eliminating the race
   *     that previously snapped the card back to its source column.
   *   - `onError` / blocked-`onSuccess` roll back to the pre-mutation
   *     snapshot.
   */
  const moveDealMutation = useMutation({
    mutationFn: async (args: MoveDealArgs) => {
      const result = await moveDeal(args);
      if (result.status === "blocked") {
        const labels = (result.blockers ?? []).map((b) => `"${b}"`).join(", ");
        const plural = (result.blockers ?? []).length > 1 ? "s" : "";
        toast.error(
          `Can't advance — complete required item${plural}: ${labels}`,
        );
      }
      return result;
    },
    onMutate: async (args: MoveDealArgs) => {
      const key = ["pipeline_properties", currentWorkspace?.id] as const;
      // Snapshot + write optimistically BEFORE awaiting cancelQueries.
      // Awaiting first inserts a microtask gap that lets the post-drag
      // render run with the old cache, briefly flashing the card back to
      // its source column before the optimistic write lands.
      const previous = queryClient.getQueryData<SavedProperty[]>(key);
      if (previous) {
        queryClient.setQueryData<SavedProperty[]>(
          key,
          previous.map((p) =>
            p.id === args.pipelinePropertyId
              ? { ...p, pipeline_id: args.toPipeline.id }
              : p,
          ),
        );
      }
      // Now cancel any in-flight refetch so it can't overwrite the
      // optimistic state when it resolves.
      await queryClient.cancelQueries({ queryKey: key });
      return { previous, key };
    },
    onSuccess: (result, args, context) => {
      if (result.status === "ok" && result.row) {
        toast.success(`Moved to ${args.toPipeline.title}`);
        // Replace the optimistic stub with the authoritative server row.
        // No refetch — we already have the latest version of this row, and
        // refetching the full list here is what was racing the optimistic
        // cache write and producing the snap-back.
        const fresh = result.row;
        queryClient.setQueryData<SavedProperty[]>(context!.key, (prev) =>
          (prev ?? []).map((p) => (p.id === fresh.id ? fresh : p)),
        );
        // The move appended a stage-history row — the analytics ("avg time in
        // stage") and the Timeline tab read it. Background refetch: the cards
        // don't gate on it, so nothing flashes.
        void queryClient.invalidateQueries({
          queryKey: STAGE_HISTORY_QUERY_PREFIX,
        });
      } else if (result.status === "blocked" && context?.previous) {
        // Gate fired — undo the optimistic move so the card snaps back.
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onError: (err: unknown, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(toUserMessage(err, "Failed to move deal"));
    },
  });

  /**
   * Move a deal into the Closed-Won stage with the acquisition modal's answers.
   *
   * Same required-checklist gate as `moveDeal`, then a single
   * `pipeline_properties` UPDATE that performs the move AND stamps ownership
   * (`is_owned` / `owned_date`) + the confirmed `purchase_price`. Finally it
   * upserts `property_setting` (purchase price + the acquisition loan blob with
   * its EMI `loan_date`) and invalidates the owned/financing/deal caches so the
   * Properties + Financing pages pick the deal up immediately.
   *
   * Ordering matters: both gates run first, so a refused move writes nothing.
   * The sold check goes before the checklist one — no amount of checklist work
   * makes a property someone else bought acquirable.
   */
  const moveToClosedWonMutation = useMutation({
    mutationFn: async (args: MoveToClosedWonArgs): Promise<MoveDealResult> => {
      const listingStatus = await fetchListingStatus(args.pipelinePropertyId);
      if (blocksAcquisition(listingStatus)) {
        return { status: "sold", listingStatus };
      }

      if (isAdvance(args.fromPipeline, args.toPipeline)) {
        const blockers = await fetchAdvanceBlockers(
          args.fromPipeline.id,
          args.pipelinePropertyId,
        );
        if (blockers.length > 0) return { status: "blocked", blockers };
      }

      // Move + mark owned + purchase price in one RETURNING update (same
      // 0-rows-affected guard as moveDeal — a silently denied RLS write must
      // surface as an error so the optimistic cache rolls back).
      const { data: updatedRow, error: updErr } = (await supabase
        .from("pipeline_properties")
        .update({
          pipeline_id: args.toPipeline.id,
          is_owned: true,
          owned_date: args.ownedDate,
          purchase_price: args.purchasePrice,
        } as never)
        .eq("id", args.pipelinePropertyId)
        .select("*")
        .single()) as PostgrestSingleResponse<SavedProperty>;
      if (updErr) throw updErr;
      if (!updatedRow) {
        throw new Error(
          "Move failed: the deal was not found or you don't have permission to update it.",
        );
      }

      // Audit log (best-effort).
      if (user?.id) {
        const { error: histErr } = await supabase
          .from("pipeline_stage_history")
          .insert({
            pipeline_property_id: args.pipelinePropertyId,
            from_pipeline_id: args.fromPipeline.id,
            to_pipeline_id: args.toPipeline.id,
            changed_by: user.id,
            workspace_id: currentWorkspace?.id ?? null,
          } as never);
        if (histErr) console.error("pipeline_stage_history insert:", histErr);
      }

      // Persist purchase price + acquisition loan into property_setting.
      if (args.dealSettingsPatch && currentWorkspace?.id) {
        const { purchase_price, flip, brrrr, rental } = args.dealSettingsPatch;
        const dbPatch: Record<string, unknown> = {
          ...(purchase_price !== undefined ? { purchase_price } : {}),
          ...splitStrategySettings({ flip, brrrr, rental }),
        };
        const client = supabase as unknown as {
          from: (t: string) => {
            select: (s: string) => {
              eq: (col: string, v: string) => {
                maybeSingle: () => DbResp<{ id: string } | null>;
              };
            };
            update: (v: unknown) => {
              eq: (col: string, v: string) => DbResp<unknown>;
            };
            insert: (v: unknown) => DbResp<unknown>;
          };
        };
        const { data: existing, error: lookupErr } = await client
          .from("property_setting")
          .select("id")
          .eq("pipeline_property_id", args.pipelinePropertyId)
          .maybeSingle();
        if (lookupErr) throw lookupErr;
        if (existing?.id) {
          const { error } = await client
            .from("property_setting")
            .update(dbPatch)
            .eq("id", existing.id);
          if (error) throw error;
        } else {
          const { error } = await client.from("property_setting").insert({
            ...dbPatch,
            pipeline_property_id: args.pipelinePropertyId,
            workspace_id: currentWorkspace.id,
            created_by: user?.id ?? null,
          });
          if (error) throw error;
        }
      }

      // Materialise the financing row in `loans` now that the property is owned
      // — this is the calculation source of truth (Financing / Properties /
      // cashflow read it). A metadata-only row: loan_type by strategy +
      // is_enabled; all term columns fall back to their DB defaults. Upsert by
      // pipeline_property_id so re-entering Closed-Won doesn't duplicate it.
      if (args.financing && currentWorkspace?.id && user?.id) {
        const { data: existingLoan, error: loanLookupErr } = await supabase
          .from("loans")
          .select("id")
          .eq("pipeline_property_id", args.pipelinePropertyId)
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle();
        if (loanLookupErr) throw loanLookupErr;
        if (existingLoan?.id) {
          const { error } = await supabase
            .from("loans")
            .update({
              loan_type: args.financing.loan_type,
              is_enabled: args.financing.enabled,
            } as never)
            .eq("id", existingLoan.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from("loans").insert({
            workspace_id: currentWorkspace.id,
            pipeline_property_id: args.pipelinePropertyId,
            created_by_user_id: user.id,
            loan_type: args.financing.loan_type,
            status: "pending",
            is_enabled: args.financing.enabled,
          } as never);
          if (error) throw error;
        }
      }

      return { status: "ok", row: updatedRow };
    },
    onMutate: async (args: MoveToClosedWonArgs) => {
      const key = ["pipeline_properties", currentWorkspace?.id] as const;
      const previous = queryClient.getQueryData<SavedProperty[]>(key);
      if (previous) {
        queryClient.setQueryData<SavedProperty[]>(
          key,
          previous.map((p) =>
            p.id === args.pipelinePropertyId
              ? ({
                  ...p,
                  pipeline_id: args.toPipeline.id,
                  purchase_price: args.purchasePrice,
                  is_owned: true,
                  owned_date: args.ownedDate,
                } as SavedProperty)
              : p,
          ),
        );
      }
      await queryClient.cancelQueries({ queryKey: key });
      return { previous, key };
    },
    onSuccess: (result, args, context) => {
      if (result.status === "ok" && result.row) {
        toast.success(`Moved to ${args.toPipeline.title}`);
        const fresh = result.row;
        queryClient.setQueryData<SavedProperty[]>(context!.key, (prev) =>
          (prev ?? []).map((p) => (p.id === fresh.id ? fresh : p)),
        );
        // The deal is now owned → surfaces on Properties + Financing.
        const wid = currentWorkspace?.id;
        void queryClient.invalidateQueries({
          queryKey: ["pipeline_properties_owned", wid],
        });
        void queryClient.invalidateQueries({
          queryKey: ["property_settings_owned", wid],
        });
        void queryClient.invalidateQueries({ queryKey: ["loans_owned", wid] });
        void queryClient.invalidateQueries({
          queryKey: ["rehab_projects_owned", wid],
        });
        void queryClient.invalidateQueries({
          queryKey: ["deal_settings", args.pipelinePropertyId],
        });
        void queryClient.invalidateQueries({
          queryKey: ["deal_loans", args.pipelinePropertyId],
        });
        void queryClient.invalidateQueries({
          queryKey: STAGE_HISTORY_QUERY_PREFIX,
        });
      } else if (result.status === "sold") {
        const label = LISTING_STATUS_LABELS[
          result.listingStatus ?? "sold"
        ].toLowerCase();
        toast.error(
          `Can't close — the listing is ${label}. Another buyer already closed on this property.`,
        );
        if (context?.previous) {
          queryClient.setQueryData(context.key, context.previous);
        }
      } else if (result.status === "blocked") {
        const labels = (result.blockers ?? []).map((b) => `"${b}"`).join(", ");
        const plural = (result.blockers ?? []).length > 1 ? "s" : "";
        toast.error(
          `Can't advance — complete required item${plural}: ${labels}`,
        );
        if (context?.previous) {
          queryClient.setQueryData(context.key, context.previous);
        }
      }
    },
    onError: (err: unknown, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(toUserMessage(err, "Failed to move deal"));
    },
  });

  return {
    moveDeal,
    moveDealMutation,
    moveToClosedWonMutation,
    /** Pure helper exposed for unit testing + direct UI use (kc-required badge). */
    findUnmetRequired,
  };
}
